/**
 * Platform detection for keyboard handling — the single place that decides
 * whether ⌘ or Ctrl is this machine's primary shortcut modifier. Both the
 * matcher (`useShortcut`) and the display layer (`formatKeys`) read from here,
 * so what the overlay advertises and what actually fires can't drift.
 */

/** True on Apple platforms, where ⌘ — not Ctrl — is the primary modifier. */
export const isMac = (): boolean =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

/**
 * Resolve react-hotkeys-hook's `mod` alias to a concrete modifier.
 *
 * The library documents `mod` as "meta on macOS and ctrl on other platforms",
 * but its matcher does no platform check at all — it accepts *either* modifier
 * on every platform. On a Mac that silently binds Ctrl-<key> alongside
 * ⌘-<key>, swallowing the Emacs-style editing keys macOS gives every text
 * field (Ctrl-K kill-to-end-of-line, Ctrl-D delete-forward, Ctrl-B
 * back-one-character) — and `useShortcut` binds modifier combos with
 * `preventDefault` inside inputs, so the native behavior is lost rather than
 * merely shadowed. Expanding the alias ourselves keeps each shortcut on the one
 * modifier the overlay actually shows.
 *
 * `mac` is a parameter so callers can resolve for a platform other than the
 * current one; it defaults to this machine's.
 */
export const resolveMod = (combo: string, mac: boolean = isMac()): string =>
  combo.replace(
    /(^|\+)mod(?=\+|$)/gi,
    (_match, prefix: string) => `${prefix}${mac ? "meta" : "ctrl"}`,
  );
